import { memo, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { IVersionHistoryItem } from 'store/types/aiVideo'
import AIPromptText from 'components/AIPromptText'
import { AiGenerationLoadingContent } from 'components/AIGenerationLoadingContent'
import { formatVideoDuration } from 'components/RecentlyUsed/VideoItem/utils'
import ActionsOverlay from 'common-components/ActionsOverlay'
import RegenerateIcon from 'assets/svgr-icons/Regenerate.svg'
import DownloadIcon from 'assets/svgr-icons/Download.svg'
import { translate } from 'utils/translations'
import { downloadMedia } from 'utils'
import { T } from 'consts/translationKeys'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { TabKey } from 'containers/SubSidebar/AIVideoContent/consts'
import { useAddAiItemToTimeline } from 'hooks/useAddAiItemToTimeline'
import { useGenerationProgress } from 'hooks/useGenerationProgress'
import { dispatch } from 'store'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { editorThunks } from 'store/features/editor/thunks'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectSelectors } from 'store/features/project/selectors'
import { PROJECT_MEDIA_TYPES } from 'consts'
import { SQUARE_ASPECT_RATIO, normalizeAspectRatio } from 'utils/aspectRatio'
import useAutoPlayWhenVisible from '../../components/VideoItem/useAutoPlayWhenVisible'
import * as Styled from './styled'
import { convertHistoryVideoVersionToBaseMediaItem } from '../utils'
import { convertHistoryVersionImgToBaseMediaItem } from 'containers/SubSidebar/AiImageContent/HistoryTab/Content/Image/utils'
import VersionHistoryItemContent from './ItemContent'
import { useSidebarItemDrag } from 'hooks/useSidebarItemDrag'

interface IVersionHistoryItemProps {
  item: IVersionHistoryItem
}

const VersionHistoryItem = ({ item }: IVersionHistoryItemProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const addItemToTimeline = useAddAiItemToTimeline()

  const isGenAiProject = !!useSelector(projectSelectors.meta)?.genAiProjectId
  const activeTimelineItem = useSelector(editorSelectors.activeTimelineItem)

  const isProcessing = !!item.process

  const { progress, message } = useGenerationProgress({
    assetId: isProcessing ? item.id : undefined,
    isGenerating: isProcessing
  })

  useAutoPlayWhenVisible(containerRef, videoRef, isLoading)

  const handleLoad = () => setIsLoading(false)

  const handleMouseEnter = () => {
    if (item.type === 'video' && videoRef.current && !isLoading) {
      videoRef.current.play()
    }
  }

  const handleMouseLeave = () => {
    if (item.type === 'video' && videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }

  const baseItem =
    item.type === 'video'
      ? convertHistoryVideoVersionToBaseMediaItem(item)
      : convertHistoryVersionImgToBaseMediaItem(item)

  const handleAddToTimeline = useCallback(() => {
    addItemToTimeline(baseItem)
  }, [addItemToTimeline, baseItem])

  const handleRegenerate = useCallback(() => {
    if (item.type === 'video') {
      if (
        isGenAiProject &&
        activeTimelineItem.id &&
        activeTimelineItem.type === PROJECT_MEDIA_TYPES.aiVideo
      ) {
        // In gen-ai projects the version history belongs to the active aiVideo
        // scene; route through the shared regeneration flow so it opens smart
        // edit when eligible. Pass the clicked version's id so the form is
        // populated from that version, not the scene's currently-applied one.
        dispatch(editorThunks.openAiVideoRegeneration(item.id))
        dispatch(aiVideoThunks.setShowVersionHistoryTab(true))
        return
      }
      dispatch(aiVideoThunks.setGenerationParamsByAssetId(item.id))
      dispatch(aiVideoThunks.setActiveTab(TabKey.GENERATE))
      dispatch(aiVideoThunks.setShowVersionHistoryTab(true))
    } else {
      if (!item.assetId) return
      dispatch(aiImageThunks.setGenerationParamsByAssetId(Number(item.assetId)))
      dispatch(editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_IMAGE))
    }
  }, [item, isGenAiProject, activeTimelineItem])

  const handleDownload = useCallback(async () => {
    const fileName =
      item.type === 'video'
        ? `ai-video-${item.id}`
        : `ai-image-${item.assetId ?? item.id}`
    await downloadMedia({ filePath: item.url, fileName })
  }, [item])

  const { setNodeRef, listeners, attributes, style } = useSidebarItemDrag(
    isProcessing ? null : baseItem
  )

  return (
    <Styled.VersionItemContainer ref={containerRef}>
      {item.prompt && <AIPromptText promptText={item.prompt} />}
      {(item.modelName ||
        item.modelQuality ||
        (item.type === 'video' && item.durationSeconds !== null)) && (
        <Styled.MetadataBadges>
          {item.modelName && (
            <Styled.MetadataBadge>{item.modelName}</Styled.MetadataBadge>
          )}
          {item.modelQuality && (
            <Styled.MetadataBadge>{item.modelQuality}</Styled.MetadataBadge>
          )}
          {item.type === 'video' && item.durationSeconds !== null && (
            <Styled.MetadataBadge>
              {formatVideoDuration(item.durationSeconds ?? 0)}
            </Styled.MetadataBadge>
          )}
        </Styled.MetadataBadges>
      )}
      {isProcessing ? (
        <AiGenerationLoadingContent
          overlay
          progress={progress}
          message={message}
          aspectRatio={normalizeAspectRatio(
            item.aspectRatio || SQUARE_ASPECT_RATIO
          )}
        />
      ) : (
        <Styled.MediaWrapper
          ref={setNodeRef}
          style={style}
          {...listeners}
          {...attributes}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <VersionHistoryItemContent
            item={item}
            isLoading={isLoading}
            videoRef={videoRef}
            handleLoad={handleLoad}
          />
          {!isLoading && (
            <ActionsOverlay
              actions={{
                mainAction: handleAddToTimeline,
                primaryActions: [
                  {
                    key: T.RE_GENERATE,
                    title: translate(T.RE_GENERATE),
                    icon: <RegenerateIcon />,
                    onClick: handleRegenerate
                  },
                  {
                    key: T.DOWNLOAD,
                    title: translate(T.DOWNLOAD),
                    icon: <DownloadIcon />,
                    onClick: handleDownload
                  }
                ]
              }}
            />
          )}
        </Styled.MediaWrapper>
      )}
    </Styled.VersionItemContainer>
  )
}

export default memo(VersionHistoryItem)
